import RequestMerger from './request-merger'

/**
 * A class that merges product requests.
 */
class ProductRequestMerger {
    /**
     * @param {{Boolean} cache - True to use memory cache or false to disable caching
     * @param {Function} getProducts - The function to get products, usually api.shopperProducts.getProducts
     * @param {Object} extraParams - Extra parameters to pass to the getProducts function
     * @param {number} [throttleInterval=0] - The interval to throttle requests
     */
    constructor({cache, getProducts, extraParams, throttleInterval = 0}) {
        this._cache = cache ? new Map() : null
        this._requestMerger = new RequestMerger({
            throttleInterval: throttleInterval,
            onExecute: async (ids) => {
                const response = await getProducts({
                    parameters: {
                        ...extraParams,
                        ids: ids.join(',')
                    }
                })

                // convert the array of products to a map of products for faster splitting
                return Object.fromEntries(
                    response.data?.map((product) => [product.id, product]) || []
                )
            },
            onSplit: (response, id) => response[id]
        })
    }

    /**
     * Get a product by id
     * @param {string} productId - The product id
     * @return {Promise<Object>} - A promise that resolves with the product data
     */
    getProduct(productId) {
        if (this._cache?.has(productId)) {
            return this._cache.get(productId)
        }
        const response = this._requestMerger.queue(productId)
        if (this._cache) {
            this._cache.set(productId, response)
        }
        return response
    }

    /**
     * Get multiple products by id
     * @param {string[]} productIds - The product ids
     * @return {Promise<Object[]>} - A promise that resolves with an array of product data
     */
    getProducts(productIds) {
        return Promise.all(productIds.map((productId) => this.getProduct(productId)))
    }
}

export default ProductRequestMerger
